import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { FileText, Plus } from 'lucide-react';

import { t } from '@/shared/i18n';
import { Tooltip } from '@/shared/ui/Tooltip';

import { ConfirmDialog } from '@/shared/ui/Dialog';

import { PageTree } from './PageTree';
import { usePages } from './usePages';
import styles from './SidebarPages.module.scss';

interface Props {
  workspace: string;
  workspaceID: string | undefined;
  canEdit: boolean;
  onNavigate?: (() => void) | undefined;
}

/**
 * Раздел «Страницы» в сайдбаре: дерево страниц организации и «+».
 * Страницы проектов живут на вкладке проекта — иначе сайдбар превратится
 * в Notion-дерево, от которого концепт отказался (§3).
 */
export function SidebarPages({ workspace, workspaceID, canEdit, onNavigate }: Props) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const pages = usePages({ workspace, workspaceID });
  const match = new RegExp(`^/app/w/${workspace}/p/([^/]+)`).exec(pathname);
  const activeID = match?.[1];
  const [deleting, setDeleting] = useState<string | null>(null);
  const deletingTitle = pages.items.find((item) => item.id === deleting)?.title ?? '';

  const open = (id: string) => {
    onNavigate?.();
    void navigate({ to: '/app/w/$slug/p/$pageId', params: { slug: workspace, pageId: id } });
  };

  const create = async (parentID?: string) => {
    const created = await pages.create(parentID);

    if (created) {
      open(created.id);
    }
  };

  return (
    <section className={styles['section']} aria-label={t.pages.title}>
      <h2 className={styles['title']}>
        <FileText />
        <span>{t.pages.title}</span>
        {canEdit && (
          <Tooltip content={t.pages.add} side="right">
            <button
              type="button"
              className={styles['add']}
              aria-label={t.pages.add}
              onClick={() => {
                void create();
              }}
            >
              <Plus />
            </button>
          </Tooltip>
        )}
      </h2>
      <div className={styles['tree']}>
        <PageTree
          compact
          items={pages.items}
          activeID={activeID}
          canEdit={canEdit}
          href={(id) => `/app/w/${workspace}/p/${id}`}
          onOpen={open}
          onCreate={(parentID) => {
            void create(parentID);
          }}
          onMove={(id, parentID, afterID) => {
            void pages.move(id, parentID, afterID);
          }}
          onDelete={canEdit ? setDeleting : undefined}
        />
        <ConfirmDialog
          open={deleting !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDeleting(null);
            }
          }}
          title={t.pages.deleteTitle(deletingTitle === '' ? t.pages.untitled : deletingTitle)}
          description={t.pages.deleteText}
          confirmLabel={t.pages.delete}
          onConfirm={async () => {
            if (deleting === null) {
              return;
            }

            await pages.remove(deleting);

            if (deleting === activeID) {
              void navigate({ to: '/app/w/$slug', params: { slug: workspace } });
            }
          }}
        />
        {pages.items.length === 0 && pages.query.data !== undefined && (
          <p className={styles['empty']}>{t.pages.empty}</p>
        )}
      </div>
    </section>
  );
}
